import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    CircularProgress,
    Alert,
} from '@mui/material';
import { StaffService } from '../api/services/staffService';

const LEAVE_TYPES = ['Annual', 'Sick', 'Unpaid', 'Maternity', 'Paternity', 'Other'];

const defaultState = {
    leaveType: 'Annual',
    startDate: '',
    endDate: '',
    reason: ''
};

export default function RequestLeaveModal({ open, onClose, onSuccess }) {
    const [formData, setFormData] = useState(defaultState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (open) {
            setFormData(defaultState);
            setErrors({});
            setSubmitError('');
        }
    }, [open]);

    const validate = () => {
        const newErrors = {};
        if (!formData.leaveType) newErrors.leaveType = 'Leave type is required';
        if (!formData.startDate) newErrors.startDate = 'Start date is required';
        if (!formData.endDate) newErrors.endDate = 'End date is required';
        if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
            newErrors.endDate = 'End date cannot be before start date';
        }
        if (!formData.reason.trim()) newErrors.reason = 'Reason is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const payload = {
                leaveType: formData.leaveType,
                startDate: formData.startDate,   // already YYYY-MM-DD from date input
                endDate: formData.endDate,
                reason: formData.reason.trim()
            };

            await StaffService.requestLeave(payload);
            onSuccess('Leave request submitted successfully');
            onClose();
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Failed to submit leave request. Please try again.';
            setSubmitError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Apply for Leave</DialogTitle>
            <DialogContent dividers>
                {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                    <FormControl required error={!!errors.leaveType} disabled={isSubmitting}>
                        <InputLabel>Leave Type</InputLabel>
                        <Select
                            value={formData.leaveType}
                            label="Leave Type"
                            onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                        >
                            {LEAVE_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            type="date"
                            label="Start Date"
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.startDate}
                            helperText={errors.startDate}
                            required
                            disabled={isSubmitting}
                            fullWidth
                        />
                        <TextField
                            type="date"
                            label="End Date"
                            value={formData.endDate}
                            inputProps={{ min: formData.startDate }}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.endDate}
                            helperText={errors.endDate}
                            required
                            disabled={isSubmitting}
                            fullWidth
                        />
                    </Box>

                    <TextField
                        label="Reason"
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        multiline
                        rows={4}
                        placeholder="Briefly explain the reason for your leave..."
                        error={!!errors.reason}
                        helperText={errors.reason}
                        required
                        disabled={isSubmitting}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
